import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const steps = [
    {
      icon: '🎯',
      title: 'Bem-vindo ao SinalInvest!',
      description: 'Você agora recebe alertas personalizados sobre as empresas que escolheu.',
      details: 'Prepare-se para transformar notícias em oportunidades reais no mercado de ações.'
    },
    {
      icon: '🤖',
      title: 'IA Analisando Notícias',
      description: 'Nossa inteligência artificial está processando milhares de notícias em tempo real.',
      details: 'Identificamos padrões e tendências que podem impactar suas ações antes que o mercado reaja.'
    },
    {
      icon: '⚡',
      title: 'Alertas Instantâneos',
      description: 'Receba notificações imediatas sobre mudanças importantes.',
      details: 'Configure alertas personalizados para as empresas que você acompanha e nunca perca uma oportunidade.'
    },
    {
      icon: '📊',
      title: 'Dashboard Inteligente',
      description: 'Acompanhe todas as informações em um painel centralizado.',
      details: 'Visualize análises, impactos e tendências de forma clara e organizada.'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      if (currentStep < steps.length - 1) {
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentStep(prev => prev + 1);
          setIsAnimating(false);
        }, 300);
      }
    }, 3000);

    return () => clearInterval(timer);
  }, [currentStep, steps.length]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 300);
    } else {
      handleFinish();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleFinish = () => {
    navigate('/dashboard');
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const currentStepData = steps[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-700 to-blue-900 flex items-center justify-center py-8 relative overflow-hidden">
      <div className="max-w-xl w-full mx-auto px-4 relative z-10">
        <div className="mb-12">
          <div className="w-full h-1 bg-white/20 rounded-full overflow-hidden mb-6">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-green-700 rounded-full transition-all duration-500"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
          <div className="flex justify-center gap-3">
            {steps.map((_, index) => (
              <div 
                key={index}
                className={`w-3 h-3 rounded-full bg-white/30 transition-all duration-300 ${index <= currentStep ? 'bg-white scale-125' : ''}`}
              ></div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 text-center relative overflow-hidden">
          <div className={`transition-all duration-300 ${isAnimating ? 'opacity-0 translate-x-5' : ''}`}>
            <div className="text-6xl mb-8 animate-bounce">
              {currentStepData.icon}
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {currentStepData.title}
            </h1>
            
            <p className="text-xl text-gray-600 mb-4 leading-relaxed">
              {currentStepData.description}
            </p>
            
            <p className="text-base text-gray-600 mb-8 leading-relaxed">
              {currentStepData.details}
            </p>

            {currentStep === 0 && (
              <div className="flex flex-col gap-4 my-8 text-left">
                <div className="flex items-center gap-4 p-4 bg-slate-100 rounded-lg border border-gray-200">
                  <span className="text-xl text-blue-700">🔹</span>
                  <span className="text-gray-900 font-medium">Análise em tempo real de notícias</span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-100 rounded-lg border border-gray-200">
                  <span className="text-xl text-blue-700">🔹</span>
                  <span className="text-gray-900 font-medium">Alertas personalizados por empresa</span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-100 rounded-lg border border-gray-200">
                  <span className="text-xl text-blue-700">🔹</span>
                  <span className="text-gray-900 font-medium">Dashboard com insights inteligentes</span>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="my-8">
                <div className="relative w-56 h-32 mx-auto mb-6 bg-slate-100 rounded-lg p-4">
                  <div className="flex items-end justify-between h-20 mb-4">
                    <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md" style={{ height: '60%', animationDelay: '0s' }}></div>
                    <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md" style={{ height: '80%', animationDelay: '0.2s' }}></div>
                    <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md" style={{ height: '45%', animationDelay: '0.4s' }}></div>
                    <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md" style={{ height: '90%', animationDelay: '0.6s' }}></div>
                    <div className="w-5 bg-gradient-to-br from-green-500 to-green-700 rounded-t-md" style={{ height: '70%', animationDelay: '0.8s' }}></div>
                  </div>
                  <div className="absolute top-1/2 inset-x-4 h-0.5 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full"></div>
                </div>
                <div className="text-sm text-gray-600">
                  <p className="my-1">Processando 1.247 notícias...</p>
                  <p className="my-1">Identificando 23 oportunidades...</p>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="my-8">
                <div className="flex flex-col gap-4 max-w-xs mx-auto">
                  <div className="flex items-center gap-4 p-4 bg-slate-100 rounded-lg border border-gray-200 animate-slideInRight">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl bg-green-100 text-green-500">📈</div>
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 m-0 mb-1">Petrobras +5.2%</h4>
                      <p className="text-sm text-gray-600 m-0">Nova descoberta de petróleo</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-slate-100 rounded-lg border border-gray-200 animate-slideInRight">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl bg-red-100 text-red-500">📉</div>
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 m-0 mb-1">Vale -3.1%</h4>
                      <p className="text-sm text-gray-600 m-0">Queda na produção de minério</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="my-8">
                <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
                  <div className="flex flex-col items-center gap-2 p-4 bg-slate-100 rounded-lg border border-gray-200 animate-fadeInUp">
                    <div className="text-2xl w-10 h-10 rounded-full flex items-center justify-center bg-green-100">📈</div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 m-0">Positivas</h4>
                      <span className="text-2xl font-bold text-gray-900">12</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2 p-4 bg-slate-100 rounded-lg border border-gray-200 animate-fadeInUp">
                    <div className="text-2xl w-10 h-10 rounded-full flex items-center justify-center bg-red-100">📉</div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 m-0">Negativas</h4>
                      <span className="text-2xl font-bold text-gray-900">3</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2 p-4 bg-slate-100 rounded-lg border border-gray-200 animate-fadeInUp">
                    <div className="text-2xl w-10 h-10 rounded-full flex items-center justify-center bg-gray-100">➡️</div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 m-0">Neutras</h4>
                      <span className="text-2xl font-bold text-gray-900">8</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12">
            <div className="flex gap-4 justify-center mb-6">
              {currentStep > 0 && (
                <button 
                  className="px-6 py-3 rounded-lg font-semibold text-base text-blue-700 bg-white border-2 border-blue-700 hover:bg-blue-700 hover:text-white transition-all duration-200"
                  onClick={handlePrevious}
                >
                  Anterior
                </button>
              )}
              
              <button 
                className="px-8 py-4 text-lg font-semibold rounded-lg text-white bg-blue-700 border-2 border-blue-700 hover:bg-blue-800 transition-all duration-200"
                onClick={handleNext}
              >
                {currentStep === steps.length - 1 ? 'Ir para o Dashboard' : 'Próximo'}
              </button>
            </div>

            <button 
              className="bg-transparent border-none text-gray-600 text-sm cursor-pointer underline transition-colors duration-200 hover:text-gray-900"
              onClick={handleSkip}
            >
              Pular introdução
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;




